import React, { useState, useEffect } from 'react'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import dayjs from 'dayjs'
import {
	ComputerDesktopIcon,
	GlobeAltIcon,
	XMarkIcon,
	ClockIcon,
	UserGroupIcon,
} from '@heroicons/react/24/outline'
import { clearSession, startSession } from '../../services/sessionCookie'
import { shortenVersion } from '../../utils/deviceLabels'
import useBoardLayout from './useBoardLayout'

const DeviceManagementModal = ({ isOpen, isDarkMode }) => {
	const [loading, setLoading] = useState(false)
	const [currentDevice, setCurrentDevice] = useState(null)
	const [otherDevices, setOtherDevices] = useState([])
	const [teamMembers, setTeamMembers] = useState([])
	const [removingDevices, setRemovingDevices] = useState({})
	const [isLoggingOut, setIsLoggingOut] = useState(false)
	const [teamPoolData, setTeamPoolData] = useState(null)

	const token = localStorage.getItem('jwtToken')
	const user = token ? jwtDecode(token) : null
	const isSharedPool = user?.isSharedPool

	const fetchAllData = async () => {
		try {
			if (!isOpen) return
			setLoading(true)

			const currentToken = localStorage.getItem('jwtToken')
			if (!currentToken) return
			const currentUser = jwtDecode(currentToken)

			const devicesResponse = await axios.get('/api/user/devices')
			const myDevices = devicesResponse.data

			const current = myDevices.find(
				device => device.deviceId === currentUser.deviceId
			)
			const others = myDevices
				.filter(device => device.deviceId !== currentUser.deviceId)
				.sort((a, b) => new Date(a.lastCheckIn) - new Date(b.lastCheckIn))

			setCurrentDevice(current)
			setOtherDevices(others)

			if (currentUser?.isSharedPool) {
				try {
					const [teamResponse, teamDevicesResponse] = await Promise.all([
						axios.get('/api/team/my-team'),
						axios.get('/api/team/devices'),
					])

					if (teamResponse.data?.devicePool) {
						setTeamPoolData(teamResponse.data.devicePool)
					}

					if (teamDevicesResponse.data) {
						// This modal manages the website pool, so offer only teammates'
						// website sessions — removing a phone would not free a website slot.
						const otherMembers = teamDevicesResponse.data
							.map(m => ({
								...m,
								devices: m.devices.filter(d => d.deviceType === 'website'),
							}))
							.filter(m => !m.isCurrentUser && m.devices.length > 0)
						setTeamMembers(otherMembers)
					}
				} catch (err) {
					console.error('Error fetching team data:', err)
				}
			}
		} catch (error) {
			console.error('Error fetching devices:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchAllData()
	}, [isOpen])

	if (!isOpen) return null

	const refreshAfterRemoval = async () => {
		const devicesResponse = await axios.get('/api/user/devices')
		const myDevices = devicesResponse.data

		const currentToken = localStorage.getItem('jwtToken')
		if (!currentToken) return
		const currentUser = jwtDecode(currentToken)

		const current = myDevices.find(
			device => device.deviceId === currentUser.deviceId
		)
		const others = myDevices.filter(
			device => device.deviceId !== currentUser.deviceId
		)

		setCurrentDevice(current)
		setOtherDevices(others)

		let updatedPoolData = teamPoolData
		if (currentUser?.isSharedPool) {
			try {
				const [teamResponse, teamDevicesResponse] = await Promise.all([
					axios.get('/api/team/my-team'),
					axios.get('/api/team/devices'),
				])
				if (teamResponse.data?.devicePool) {
					updatedPoolData = teamResponse.data.devicePool
					setTeamPoolData(updatedPoolData)
				}
				if (teamDevicesResponse.data) {
					// This modal manages the website pool, so offer only teammates'
					// website sessions — removing a phone would not free a website slot.
					const otherMembers = teamDevicesResponse.data
						.map(m => ({
							...m,
							devices: m.devices.filter(d => d.deviceType === 'website'),
						}))
						.filter(m => !m.isCurrentUser && m.devices.length > 0)
					setTeamMembers(otherMembers)
				}
			} catch (err) {
				console.error('Error refreshing team data:', err)
			}
		}

		const shouldRefreshToken = currentUser?.isSharedPool
			? updatedPoolData &&
			  updatedPoolData.usage.websiteDevices <= updatedPoolData.limits.websiteDevices
			: myDevices.length <= (currentUser?.deviceLimit || 3)

		if (shouldRefreshToken) {
			try {
				const refreshResponse = await axios.post('/api/user/refresh-token/web')
				if (refreshResponse.data.success) {
					startSession(refreshResponse.data.token)
					window.location.reload()
				}
			} catch (error) {
				console.error('Error refreshing token after device removal:', error)
			}
		}
	}

	const handleRemoveDevice = async deviceId => {
		try {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: true }))
			await axios.post('/api/user/terminate-session', { deviceId })
			await refreshAfterRemoval()
		} catch (error) {
			console.error('Error:', error)
		} finally {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: false }))
		}
	}

	const handleRemoveTeamDevice = async (deviceId, targetUserId) => {
		try {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: true }))
			await axios.post('/api/team/terminate-device', { deviceId, targetUserId })
			await refreshAfterRemoval()
		} catch (error) {
			console.error('Error removing team device:', error)
		} finally {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: false }))
		}
	}

	const handleLogout = async () => {
		try {
			setIsLoggingOut(true)
			await axios.post('/api/user/logout-session')
			clearSession()
			localStorage.removeItem('email')
			window.location.href = '/'
		} catch (error) {
			console.log('error', error)
			clearSession()
			localStorage.removeItem('email')
			window.location.href = '/'
		}
	}

	const totalTeamOtherDevices = teamMembers.reduce(
		(sum, m) => sum + m.devices.length,
		0
	)

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
			<div
				className={`${
					isDarkMode ? 'bg-gray-800' : 'bg-white'
				} rounded-lg max-w-2xl w-full max-h-[95vh] overflow-hidden`}
			>
				<div className="p-6">
					<div className="flex justify-between items-center mb-3">
						<h2
							className={`text-${
								isDarkMode ? 'white' : 'gray-900'
							} text-xl font-bold`}
						>
							Device Management
						</h2>
						<button
							onClick={handleLogout}
							disabled={isLoggingOut}
							className={`px-4 py-2 text-xs rounded-md border transition-colors ${
								isDarkMode
									? 'border-red-500 text-red-500 hover:bg-red-500/10 disabled:opacity-50'
									: 'border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50'
							}`}
						>
							{isLoggingOut ? (
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
							) : (
								'Log Out'
							)}
						</button>
					</div>

					<div className="overflow-y-auto max-h-[70vh] pr-2">
						{loading ? (
							<div className="flex justify-center py-8">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500" />
							</div>
						) : (
							<div className="space-y-3">
								<div
									className={`p-3 rounded-md ${
										isDarkMode
											? 'bg-yellow-500/10 text-yellow-500'
											: 'bg-yellow-50 text-yellow-600'
									}`}
								>
									{isSharedPool ? (
										teamPoolData ? (
											<>
												Your team is using{' '}
												<span className="font-semibold">
													{teamPoolData.usage.websiteDevices}
												</span>{' '}
												of{' '}
												<span className="font-semibold">
													{teamPoolData.limits.websiteDevices}
												</span>{' '}
												shared website devices. Please remove devices to
												continue.
											</>
										) : (
											'Your team has reached the shared device pool limit. Please remove devices to continue.'
										)
									) : (
										`You have reached the maximum number of devices ${user?.deviceLimit || 3} per account. Please remove other devices to continue.`
									)}
								</div>

								{currentDevice && (
									<div className="space-y-3">
										<DeviceItem
											device={currentDevice}
											isCurrent={true}
											isDarkMode={isDarkMode}
										/>
									</div>
								)}

								<div
									className={`border-t ${
										isDarkMode ? 'border-gray-700' : 'border-gray-200'
									}`}
								/>

								<div className="space-y-2">
									<h3
										className={`text-sm font-medium ${
											isDarkMode ? 'text-white' : 'text-gray-900'
										}`}
									>
										Your Other Devices ({otherDevices.length})
									</h3>
									<div className="space-y-2">
										{otherDevices.map(device => (
											<DeviceItem
												key={device._id}
												device={device}
												isCurrent={false}
												isDarkMode={isDarkMode}
												onRemove={() => handleRemoveDevice(device._id)}
												isRemoving={removingDevices[device._id]}
											/>
										))}
										{otherDevices.length === 0 && (
											<p
												className={`text-xs ${
													isDarkMode ? 'text-gray-500' : 'text-gray-400'
												}`}
											>
												No other devices
											</p>
										)}
									</div>
								</div>

								{isSharedPool && teamMembers.length > 0 && (
									<>
										<div
											className={`border-t ${
												isDarkMode ? 'border-gray-700' : 'border-gray-200'
											}`}
										/>

										<div className="space-y-3">
											<div className="flex items-center gap-2">
												<UserGroupIcon
													className={`h-4 w-4 ${
														isDarkMode ? 'text-gray-400' : 'text-gray-500'
													}`}
												/>
												<h3
													className={`text-sm font-medium ${
														isDarkMode ? 'text-white' : 'text-gray-900'
													}`}
												>
													Team Members' Devices ({totalTeamOtherDevices})
												</h3>
											</div>

											{teamMembers.map(member => (
												<div key={member.userId} className="space-y-2">
													<p
														className={`text-xs font-medium ${
															isDarkMode
																? 'text-gray-400'
																: 'text-gray-500'
														}`}
													>
														{member.name} ({member.devices.length})
													</p>
													<div className="space-y-2">
														{member.devices.map(device => (
															<DeviceItem
																key={device.deviceId}
																device={device}
																isCurrent={false}
																isDarkMode={isDarkMode}
																onRemove={() =>
																	handleRemoveTeamDevice(
																		device.deviceId,
																		member.userId
																	)
																}
																isRemoving={removingDevices[device.deviceId]}
															/>
														))}
													</div>
												</div>
											))}
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

const RemoveDeviceButton = ({ onRemove, isRemoving, isDarkMode, compact }) => (
	<button
		onClick={onRemove}
		disabled={isRemoving}
		className={`${compact ? 'shrink-0 whitespace-nowrap ' : ''}p-1.5 rounded-md text-xs ${
			isDarkMode
				? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
				: 'bg-red-50 text-red-600 hover:bg-red-100'
		} transition-colors`}
	>
		{isRemoving ? (
			<div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
		) : (
			<span className="flex items-center gap-1">
				<XMarkIcon className="h-3 w-3" />
				Remove
			</span>
		)}
		<span className="sr-only">Remove device</span>
	</button>
)

/**
 * Two rows instead of three, with the login method and the build number
 * dropped: on a phone the full row wraps into an unreadable stack.
 */
const CompactDeviceItem = ({
	device,
	isCurrent,
	isDarkMode,
	onRemove,
	isRemoving,
	lastCheckIn,
}) => {
	const place = [device.city, device.country]
		.filter(part => part && part !== 'Unknown')
		.join(', ')

	return (
		<div className="flex items-center justify-between gap-3">
			<div className="min-w-0 flex-1 space-y-1">
				<div className="flex items-center gap-2">
					<GlobeAltIcon className="h-4 w-4 shrink-0" />
					<span
						className={`truncate font-medium text-${
							isDarkMode ? 'white' : 'gray-900'
						}`}
					>
						{shortenVersion(device.browser)}
					</span>
					{isCurrent && (
						<span className="whitespace-nowrap rounded-full bg-green-500/15 px-2 py-0.5 text-xs text-green-500">
							Current
						</span>
					)}
				</div>
				<div
					className={`flex items-center gap-2 text-sm text-${
						isDarkMode ? 'gray-300' : 'gray-600'
					}`}
				>
					<ComputerDesktopIcon className="h-3.5 w-3.5 shrink-0" />
					<span className="min-w-0">
						{[device.os, place, !isCurrent && lastCheckIn]
							.filter(Boolean)
							.join(' • ')}
					</span>
				</div>
			</div>
			{!isCurrent && onRemove && (
				<RemoveDeviceButton
					onRemove={onRemove}
					isRemoving={isRemoving}
					isDarkMode={isDarkMode}
					compact
				/>
			)}
		</div>
	)
}

const DeviceItem = ({
	device,
	isCurrent,
	isDarkMode,
	onRemove,
	isRemoving,
}) => {
	const lastCheckIn = dayjs(device.lastCheckIn).fromNow()
	const { isMobile, isTablet } = useBoardLayout()

	return (
		<div
			className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${
				isDarkMode ? 'border-gray-600' : 'border-gray-200'
			} rounded-lg p-3`}
		>
			{isMobile || isTablet ? (
				<CompactDeviceItem
					device={device}
					isCurrent={isCurrent}
					isDarkMode={isDarkMode}
					onRemove={onRemove}
					isRemoving={isRemoving}
					lastCheckIn={lastCheckIn}
				/>
			) : (
				<div className="flex items-center justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<GlobeAltIcon className="h-4 w-4" />
							<span
								className={`font-medium text-${
									isDarkMode ? 'white' : 'gray-900'
								}`}
							>
								{device.browser}
							</span>
							{device.loginMethod && (
								<span
									className={`px-1.5 py-0.5 text-xs rounded-full ${
										device.loginMethod === 'google'
											? isDarkMode
												? 'bg-orange-500/20 text-orange-400'
												: 'bg-orange-100 text-orange-700'
											: isDarkMode
												? 'bg-gray-500/20 text-gray-400'
												: 'bg-gray-100 text-gray-600'
									}`}
								>
									{device.loginMethod === 'google' ? 'Google Sign-In' : 'Email / Password'}
								</span>
							)}
							<span
								className={`px-2 py-0.5 text-xs rounded-full ${
									isCurrent
										? 'bg-green-500/15 text-green-500'
										: `${
												isDarkMode
													? 'bg-gray-600 text-gray-300'
													: 'bg-gray-200 text-gray-700'
										  }`
								} flex items-center gap-1`}
							>
								{isCurrent ? (
									'Current'
								) : (
									<>
										<ClockIcon className="h-3 w-3" />
										{lastCheckIn}
									</>
								)}
							</span>
						</div>
						<div
							className={`flex items-center gap-2 text-sm text-${
								isDarkMode ? 'gray-300' : 'gray-600'
							}`}
						>
							<ComputerDesktopIcon className="h-3.5 w-3.5" />
							<span>
								{device.os}
								{(device.city || device.country) && ' • '}
								{device.city && device.country ? `${device.city}, ${device.country}` : (device.city || device.country)}
							</span>
						</div>
					</div>
					{!isCurrent && onRemove && (
						<RemoveDeviceButton
							onRemove={onRemove}
							isRemoving={isRemoving}
							isDarkMode={isDarkMode}
						/>
					)}
				</div>
			)}
		</div>
	)
}

export default DeviceManagementModal
